import { EnvelopeIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
    ActionIcon,
    Avatar,
    Badge,
    Box,
    Card,
    Group,
    Paper,
    ScrollArea,
    Stack,
    Text,
    ThemeIcon,
    Title,
    useMantineColorScheme,
} from "@mantine/core";
import { useTranslation } from "../../../hooks/useTranslation";
import { formatShortDate } from "../../../utils/dateFormat";
import { toTitleCase } from "../../../utils/textFormat";
import { EmployeeOverviewListItem } from "../models/employeeOverview.model";
import { AllocationBadgeGroup } from "./AllocationBadgeGroup";

interface EmployeeOnlyPanelProps {
    employee: EmployeeOverviewListItem;
    onClose: () => void;
}

const hasRealDate = (value: string | null): boolean =>
    !!value && new Date(value).getFullYear() > 1;

/**
 * Side-panel content for employees with no linked Person (no CV) — same chrome as
 * CVPreviewSidebar/CVPreviewHeader (Paper shell, Avatar-120 header, Availability card, status
 * badges), but a separate component rather than a shared one: CVPreviewHeader is coupled to the
 * Person/CV domain model (imagePath, cvUpdateDate, ProfileCompletionSummary's own personId fetch),
 * none of which exists for a Person-less Employee. No CV-specific sections (skills, projects,
 * roles, download/edit) — there is nothing to show for them.
 */
export function EmployeeOnlyPanel({ employee, onClose }: EmployeeOnlyPanelProps) {
    const { colorScheme } = useMantineColorScheme();
    const { t } = useTranslation();
    const isDark = colorScheme === "dark";
    const displayName = `${toTitleCase(employee.firstName)} ${toTitleCase(employee.lastName)}`;

    return (
        <Paper
            radius={0}
            h="100%"
            w="100%"
            style={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: isDark ? "var(--mantine-color-dark-7)" : "var(--mantine-color-gray-0)",
                position: "relative",
                borderRadius: 0,
                borderLeft: "1px solid var(--mantine-color-gray-3)",
                boxShadow: "-5px 0 15px rgba(0, 0, 0, 0.05)",
            }}
        >
            <ActionIcon
                onClick={onClose}
                style={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}
                variant="filled"
                color="gray"
                radius="xl"
                size="lg"
                aria-label={t("EMPLOYEEOVERVIEW_CLOSE_ARIA", undefined, "Close")}
            >
                <XMarkIcon style={{ width: 20, height: 20 }} />
            </ActionIcon>

            <Box
                p="md"
                style={{
                    flexShrink: 0,
                    background: isDark ? "var(--mantine-color-dark-6)" : "var(--mantine-color-gray-1)",
                    borderBottom: "1px solid var(--mantine-color-gray-3)",
                }}
            >
                <Group gap="md" align="flex-start">
                    <Avatar
                        size={120}
                        radius="xl"
                        color="brand"
                        variant="light"
                        style={{ border: "3px solid var(--mantine-color-brand-5)", flexShrink: 0 }}
                    >
                        {employee.firstName.charAt(0)}
                        {employee.lastName.charAt(0)}
                    </Avatar>

                    <Box style={{ flex: 1, minWidth: 0 }}>
                        {hasRealDate(employee.availabilityDate) && (
                            <Card shadow="sm" p="6px 10px" radius="md" withBorder mb="xs" style={{ display: "inline-block" }}>
                                <Text size="xs" c="dimmed" mb={1}>{t("EMPLOYEEOVERVIEW_AVAILABILITY_LABEL", undefined, "Availability")}</Text>
                                <Text size="sm" fw={600} c="orange.9">{formatShortDate(employee.availabilityDate)}</Text>
                            </Card>
                        )}
                        <Group gap="xs" wrap="wrap">
                            {!employee.isActive && (
                                <Badge color="red" size="sm" variant="filled">{t("EMPLOYEEOVERVIEW_INACTIVE_BADGE", undefined, "Inactive")}</Badge>
                            )}
                        </Group>
                    </Box>
                </Group>

                <Title order={4} mt="sm" style={{ wordBreak: "break-word" }}>{displayName}</Title>
                <Text c="dimmed" size="sm" mb={2}>{employee.positionName ?? t("EMPLOYEEOVERVIEW_NO_POSITION_SPECIFIED", undefined, "No position specified")}</Text>

                <Box mt={4} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <ThemeIcon size="sm" radius="xl" color="gray" variant="light">
                        <EnvelopeIcon style={{ width: 14, height: 14 }} />
                    </ThemeIcon>
                    <Text size="sm" style={{ wordBreak: "break-all" }}>{employee.email}</Text>
                </Box>
            </Box>

            <ScrollArea style={{ flex: 1 }} offsetScrollbars scrollbarSize={8}>
                <Stack gap="md" p="md">
                    <Card withBorder radius="md" p="md">
                        <Text size="sm" fw={600} mb={4}>{t("EMPLOYEEOVERVIEW_NO_CV_TITLE", undefined, "No CV for employee")}</Text>
                        <Text size="xs" c="dimmed">
                            {t(
                                "EMPLOYEEOVERVIEW_NO_CV_DESCRIPTION",
                                undefined,
                                "This employee hasn't linked a CV Portal profile yet, so skills, projects, and other CV sections aren't available here."
                            )}
                        </Text>
                    </Card>

                    <Stack gap={4}>
                        <Text size="sm" fw={600}>{t("EMPLOYEEOVERVIEW_EMPLOYMENT_LABEL", undefined, "Employment")}</Text>
                        <Text size="sm" c="dimmed">
                            {employee.contractTypeName ?? t("EMPLOYEEOVERVIEW_EMPLOYMENT_TYPE_NOT_SPECIFIED", undefined, "Employment type not specified")}
                        </Text>
                        <Text size="sm" c="dimmed">
                            {employee.workStartDate
                                ? employee.workEndDate
                                    ? t(
                                        "EMPLOYEEOVERVIEW_WORK_RANGE",
                                        { start: formatShortDate(employee.workStartDate), end: formatShortDate(employee.workEndDate) },
                                        `Work: ${formatShortDate(employee.workStartDate)} – ${formatShortDate(employee.workEndDate)}`
                                    )
                                    : t(
                                        "EMPLOYEEOVERVIEW_WORK_SINCE",
                                        { start: formatShortDate(employee.workStartDate) },
                                        `Work since ${formatShortDate(employee.workStartDate)}`
                                    )
                                : t("EMPLOYEEOVERVIEW_WORK_DATES_NOT_SPECIFIED", undefined, "Work dates not specified")}
                        </Text>
                    </Stack>

                    <Stack gap={4}>
                        <Text size="sm" fw={600}>{t("EMPLOYEEOVERVIEW_CURRENT_ALLOCATIONS", undefined, "Current allocations")}</Text>
                        <AllocationBadgeGroup allocations={employee.allocations} />
                    </Stack>
                </Stack>
            </ScrollArea>
        </Paper>
    );
}
